/**
 * AlertsAndActions - TASK-378
 * Priority alerts card showing items requiring attention:
 * pending reviews, expiring documents, overdue requests.
 * Color-coded severity badges.
 */

import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Bell,
  CheckCircle,
  ChevronRight,
  Clock,
  FileWarning,
  ShieldAlert,
} from 'lucide-react';
import { useComplianceContext } from '../../../contexts/ComplianceContext';
import { getClientActivity } from '../../../services/complianceActivityService';
import { formatTimestamp } from '../../../utils/formatters';
import type { ComplianceActivity, ActivitySeverity } from '../../../types/complianceWorkflow';

/**
 * Severity styling for compliance activity alerts (ActivitySeverity type).
 * Intentionally distinct from the SEVERITY_CONFIG in COReviewTab — these map
 * to system-level event severities (info/warning/error/critical), NOT review
 * comment severities (info/warning/action_required).
 */
const SEVERITY_CONFIG: Record<ActivitySeverity, { dot: string; bg: string; text: string }> = {
  critical: { dot: 'bg-red-500', bg: 'bg-red-50', text: 'text-red-700' },
  error: { dot: 'bg-red-400', bg: 'bg-red-50', text: 'text-red-600' },
  warning: { dot: 'bg-amber-500', bg: 'bg-amber-50', text: 'text-amber-700' },
  info: { dot: 'bg-blue-400', bg: 'bg-blue-50', text: 'text-blue-600' },
};

const SEVERITY_ICON: Record<ActivitySeverity, React.ElementType> = {
  critical: ShieldAlert,
  error: AlertTriangle,
  warning: FileWarning,
  info: Bell,
};

export const AlertsAndActions: React.FC = () => {
  const { clientId } = useComplianceContext();
  const [alerts, setAlerts] = useState<ComplianceActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) return;

    let cancelled = false;

    async function fetchAlerts() {
      setLoading(true);
      try {
        const response = await getClientActivity(clientId, { limit: 10 });
        if (!cancelled) {
          // Filter to only actionable severity items
          const actionable = (response?.items ?? []).filter(
            (a: ComplianceActivity) => a.severity === 'critical' || a.severity === 'warning' || a.severity === 'error'
          );
          setAlerts(actionable);
        }
      } catch {
        if (!cancelled) setAlerts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAlerts();
    return () => { cancelled = true; };
  }, [clientId]);

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-40 mb-3" />
        <div className="h-4 bg-gray-100 rounded w-full mb-2" />
        <div className="h-4 bg-gray-100 rounded w-3/4" />
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
          <span className="text-green-800 font-medium">
            All compliance checks clear — no pending alerts
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-amber-50">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-600" />
          <span className="font-medium text-amber-900">
            Priority Alerts ({alerts.length})
          </span>
        </div>
        <span className="text-xs text-amber-600 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Updated just now
        </span>
      </div>

      {/* Alert list */}
      <div className="divide-y divide-gray-100">
        {alerts.map((alert) => {
          const config = SEVERITY_CONFIG[alert.severity];
          const SeverityIcon = SEVERITY_ICON[alert.severity];

          return (
            <div
              key={alert.id}
              className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className={`w-2 h-2 rounded-full shrink-0 ${config.dot}`} />
                <SeverityIcon className={`w-4 h-4 shrink-0 ${config.text}`} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {alert.description}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded font-medium ${config.bg} ${config.text}`}
                    >
                      {alert.severity}
                    </span>
                    <span className="text-xs text-gray-400">
                      {alert.created_at ? formatTimestamp(alert.created_at) : ''}
                    </span>
                  </div>
                </div>
              </div>
              <button
                disabled
                className="text-sm text-gray-400 flex items-center gap-1 shrink-0 ml-3 cursor-not-allowed"
                title="Alert detail view coming soon"
              >
                Review <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AlertsAndActions;
